import type { ActivityRow } from "../lib/api";
import { t, type Language } from "../lib/i18n";
import { ActivityRowItem } from "./ActivityRowItem";

interface ActivityTeaserProps {
  /** Last-5 unified rows (both outcomes; no-ops excluded server-side).
   * `null` while the primary load is in flight. */
  rows: ActivityRow[] | null;
  language: Language;
  /** Navigate to the Atividade tab ("Ver toda a actividade →"). */
  onSeeAll: () => void;
}

/** Overview activity teaser (§13.2): the last 5 events + a link to the full
 * Atividade tab. Count-based ("latest heartbeat" — what just happened), so
 * it deliberately ignores the RangePicker; the Atividade tab is the
 * range-windowed, filterable surface. Presentational: the rows are fetched
 * once at Dashboard level (shared with the primary load). Universal. */
export function ActivityTeaser({
  rows,
  language,
  onSeeAll,
}: ActivityTeaserProps): JSX.Element {
  return (
    <section
      aria-label={t("activity.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <div className="mb-3">
        <h2 className="text-base font-bold text-kwanza-ink">
          {t("activity.heading", language)}
        </h2>
        {/* The teaser is count-based and ignores the period picker; label
         * its own scope rather than letting the header's range read as if it
         * governed everything below it (design review finding 7). */}
        <p className="text-xs text-kwanza-inkSoft" data-testid="activity-teaser-scope">
          {t("activity.teaser_scope", language)}
        </p>
      </div>
      {rows === null ? (
        <ul aria-busy="true" data-testid="activity-skeleton">
          {[0, 1, 2, 3, 4].map((i) => (
            <li
              key={i}
              className="mb-2 h-6 animate-pulse rounded bg-kwanza-divider"
            />
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <p className="text-sm text-kwanza-inkSoft">
          {t("activity.empty", language)}
        </p>
      ) : (
        <ul className="divide-y divide-kwanza-divider">
          {rows.map((row) => (
            <ActivityRowItem
              key={`${row.timestamp}-${row.correlation_id}`}
              row={row}
              language={language}
            />
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={onSeeAll}
        data-testid="activity-see-all"
        className="mt-3 text-sm font-bold text-kwanza-accentText hover:underline"
      >
        {t("activity.see_all", language)}
      </button>
    </section>
  );
}
